import React, { useMemo } from "react";
import ProductItem from "./ProductItem";
import { useProducts } from "../context/ProductContext";
import { searchProducts } from "../utils/searchIndex";

const ProductList = ({
  searchQuery = "",
  sortBy = "featured",
  categoryFilter = "All",
  typeFilter = "all",
  priceRange = "all",
  inStockOnly = false,
  onProductCountChange,
  onSearchSelect,
}) => {
  const { products } = useProducts();

  const filteredAndSortedProducts = useMemo(() => {
    // 1. Initial set from MiniSearch or full catalog (respecting active ON/OFF toggle)
    let list = searchQuery && searchQuery.trim()
      ? searchProducts(searchQuery.trim(), 100)
      : [...products];

    // Filter out turned-off items (active: false)
    list = list.filter((p) => p.active !== false);

    // 2. Category Filter (Case-insensitive check for "all")
    if (categoryFilter && categoryFilter.toLowerCase() !== "all") {
      const normTarget = categoryFilter.toLowerCase().replace(/[^a-z0-9]/g, "");
      list = list.filter((p) => {
        const normCat = (p.category || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        const normTags = (p.tags || []).map((t) => t.toLowerCase().replace(/[^a-z0-9]/g, ""));
        return normCat.includes(normTarget) || normTarget.includes(normCat) || normTags.some((t) => t.includes(normTarget));
      });
    }

    // 3. Type Filter (Custom vs Ready to Ship)
    if (typeFilter === "custom") {
      list = list.filter((p) => p.itemType === "custom" || p.isCustomizable);
    } else if (typeFilter === "ready-to-ship") {
      list = list.filter((p) => p.itemType === "ready-to-ship" || !p.isCustomizable);
    }

    // 4. In-Stock Filter
    if (inStockOnly) {
      list = list.filter((p) => p.inStock !== false);
    }

    // 5. Price Range Filter
    if (priceRange !== "all") {
      list = list.filter((p) => {
        const price = typeof p.priceNum === "number" && !isNaN(p.priceNum)
          ? p.priceNum
          : parseFloat(String(p.price || "").replace(/[^0-9.]+/g, "")) || 0;

        switch (priceRange) {
          case "under-5":
            return price < 5;
          case "5-10":
            return price >= 5 && price <= 10;
          case "10-20":
            return price > 10 && price <= 20;
          case "20-plus":
            return price > 20;
          default:
            return true;
        }
      });
    }

    // 6. Sorting
    const sorted = [...list];
    sorted.sort((a, b) => {
      const priceA = typeof a.priceNum === "number" && !isNaN(a.priceNum)
        ? a.priceNum
        : parseFloat(String(a.price || "").replace(/[^0-9.]+/g, "")) || 0;
      const priceB = typeof b.priceNum === "number" && !isNaN(b.priceNum)
        ? b.priceNum
        : parseFloat(String(b.price || "").replace(/[^0-9.]+/g, "")) || 0;

      switch (sortBy) {
        case "price-asc":
          return priceA - priceB;
        case "price-desc":
          return priceB - priceA;
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        case "newest":
          return String(b.id).localeCompare(String(a.id));
        case "featured":
        default:
          return 0; // Natural catalog sequence
      }
    });

    return sorted;
  }, [products, searchQuery, sortBy, categoryFilter, typeFilter, priceRange, inStockOnly]);

  // Report count up if requested
  React.useEffect(() => {
    if (onProductCountChange) {
      onProductCountChange(filteredAndSortedProducts.length);
    }
  }, [filteredAndSortedProducts.length, onProductCountChange]);

  return (
    <div className="product-grid">
      {filteredAndSortedProducts.length > 0 ? (
        filteredAndSortedProducts.map((product) => (
          <ProductItem key={product.id} product={product} />
        ))
      ) : (
        <div className="no-products-container">
          <p className="no-products">No products found matching your criteria.</p>
          <div className="no-products-suggestions">
            <span className="suggestion-label">Try searching:</span>
            <div className="suggestion-pills">
              {["BTS", "Stickers", "Tote Bag", "Photocard", "Keychains", "Pins"].map((term) => (
                <button
                  key={term}
                  type="button"
                  className="suggestion-pill"
                  onClick={() => onSearchSelect && onSearchSelect(term)}
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductList;
